import { Component, OnInit } from '@angular/core';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { BillingReportService } from '@admin/billing/billing-report/shared/services/billing-report.service';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { CustomerListFilterModel } from '@admin/billing/billing-report/shared/models/customer-list-filter-model';
import { CustomerListItemModel } from '@admin/billing/billing-report/shared/models/customer-list-item-model';
import { LazyLoadEvent } from 'primeng/api';
import { PortfolioService } from '@admin/portfolios/shared/services/portfolio.service';
import { PortfolioAccountManagerAdminModel } from '@admin/portfolios/shared/models/portfolio-account-manager-admin-model';
import { CustomerListTotals } from '@admin/billing/billing-report/shared/models/customer-list-totals';
import { CustomerStatusEnum } from '../../../customers/shared/models/customer-status.enum';
import { ThreadRelatedObjectType } from '@admin/shared/models/comment-models/thread-related-object-type.enum';
import { CommentsService } from '../../../shared/services/comments.service';
import { ThreadTopic } from '../../../shared/models/comment-models/thread-topic.enum';
import { PostingChannel } from '../../../shared/models/comment-models/posting-channel.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-customer-list',
  templateUrl: './customer-list.component.html',
  styleUrls: ['./customer-list.component.less'],
  standalone: false,
})
export class CustomerListComponent implements OnInit {
  public filterPagingWrapper: PagingFilter<CustomerListFilterModel>;

  public customers: CustomerListItemModel[];

  public listTotals: CustomerListTotals;

  public totalRecords: number;
  public pageSize: number;
  public specialDateFormat: string;
  public dateFormat: string;

  public accountManagers: PortfolioAccountManagerAdminModel[];

  public ThreadTopicEnum = ThreadTopic;
  public ThreadRelatedObjectTypeEnum = ThreadRelatedObjectType;
  public PostingChannelEnum = PostingChannel;

  public displayedOrEditingThreadId: number;
  public editingThreadObjectId: number;
  public displayedOrEditingThreadTopic: number;
  public displayThread: boolean;
  public displayThreadPostEditor: boolean;

  public decimalFormat: string;

  constructor(
    private config: ConfigurationService,
    private translateService: TranslateService,
    private portfolioService: PortfolioService,
    private reportService: BillingReportService,
    private commentsService: CommentsService
  ) {
    this.customers = [];
    this.accountManagers = [];
    this.filterPagingWrapper = new PagingFilter(new CustomerListFilterModel());
  }

  onLoad(event: LazyLoadEvent) {
    if (event.sortField && event.sortOrder) {
      this.filterPagingWrapper.sortParameters = [
        {
          column: event.sortField,
          order: event.sortOrder == 1 ? 'desc' : 'asc',
        },
      ];
    }
    this.filterPagingWrapper.pagingInfo.start = event.first;
    this.filterPagingWrapper.pagingInfo.limit = event.rows;
    this.onFilter();
  }

  onFilter() {
    this.filterPagingWrapper.pagingInfo.start =
      this.filterPagingWrapper.pagingInfo.start || 0;

    this.reportService
      .listCustomers(this.filterPagingWrapper)
      .subscribe((l) => {
        this.customers = l.items;
        this.totalRecords = l.totalCount;

        this.loadCommentsAndTotals();
      });
  }

  loadCommentsAndTotals() {
    if (!this.customers || this.customers.length == 0) return;

    var customerIds = this.customers.map((c) => c.customerId);
    this.commentsService
      .FilterThreadPreviews(ThreadRelatedObjectType.Customer, customerIds)
      .subscribe((d) => {
        let itemsDict = d.items;
        this.customers.forEach((c) => {
          let customerThreads = itemsDict[c.customerId];

          if (customerThreads) {
            let debtThread = customerThreads.filter(
              (t) => t.threadTopicId == ThreadTopic.Debt
            )[0];
            if (debtThread && debtThread.posts.length > 0)
              c.lastDebtComment = debtThread.posts[0];

            let accThread = customerThreads.filter(
              (t) => t.threadTopicId == ThreadTopic.ACC
            )[0];
            if (accThread) {
              let commonPosts = accThread.posts.filter(
                (t) => t.postingChannelId == PostingChannel.Common
              );
              let accPosts = accThread.posts.filter(
                (t) => t.postingChannelId == PostingChannel.ACC
              );

              if (commonPosts.length > 0)
                c.lastSalesAccComment = commonPosts[0];
              if (accPosts.length > 0) c.lastAccComment = accPosts[0];
            }
          }
        });
        this.loadTotals();
      });
  }

  loadTotals() {
    this.reportService
      .getCustomersTotalAmounts(this.filterPagingWrapper)
      .subscribe((r) => {
        this.listTotals = r;
      });
  }

  showThread(threadId: number, topicId: number) {
    this.displayedOrEditingThreadId = threadId;
    this.displayedOrEditingThreadTopic = topicId;
    this.displayThread = true;
  }

  showCommentEditor(threadId: number, objectId: number, topicId: number) {
    this.displayedOrEditingThreadId = threadId;
    this.editingThreadObjectId = objectId;
    this.displayedOrEditingThreadTopic = topicId;
    this.displayThreadPostEditor = true;
  }

  onClearFilter() {
    this.filterPagingWrapper = new PagingFilter(new CustomerListFilterModel());
  }

  onApplyFilter() {
    this.filterPagingWrapper.pagingInfo.start = 0;
    this.onFilter();
  }

  public onUpdateTable(updated: boolean) {
    this.displayThreadPostEditor = false;
    this.displayThread = false;
    if (updated) {
      this.onFilter();
    }
  }

  public onThreadClose(switchToAdd: boolean) {
    this.displayThread = false;
    if (switchToAdd) this.displayThreadPostEditor = true;
  }

  public onPostDelete(deleted: boolean) {
    this.onFilter();
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  getRowStyleClass(item: CustomerListItemModel) {
    return item.customerStatusId != CustomerStatusEnum.Archived
      ? ''
      : 'archived-customer-billing-report';
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();
    this.pageSize = config.defaultPageSize;
    this.specialDateFormat = this.getSpecialDateFormat(config.dateFormat);
    this.dateFormat = config.dateFormat;
    this.decimalFormat = '1.2-2';

    this.portfolioService.accountManagerList().subscribe((a) => {
      this.accountManagers = a.items;
    });
  }
}
